import type {
  AccessCodeResponse,
  AccountSummary,
  BrowserSession,
  DeletionResponse,
  Device,
  CollectionDetail,
  CollectionSummary,
  FormulaCheck,
  GateReport,
  GroupMatchResponse,
  ImportResponse,
  PreviewResult,
  PublishDiffResponse,
  ItemDeleteRefusal,
  ItemDraft,
  ItemReference,
  LibraryItem,
  PairingRequest,
  PublicMeta,
  PublishResponse,
  RecoveryCodeResponse,
  Revision,
} from "./types";

/** Anything the server refused, with the status so callers can branch. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    /// The whole refusal body when there is one — a 409 from the item library
    /// carries the templates that still reference the item, and dropping it
    /// would turn a helpful refusal into "conflict".
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Sentinel for a poll that is still pending — a 204 is not an error. */
export const PENDING = Symbol("pending");

function csrfToken(): string | null {
  // Double-submit: the cookie is readable on purpose, and echoing it in a header
  // is what a cross-origin page cannot do.
  const match = document.cookie.match(/(?:^|;\s*)dit_csrf=([^;]*)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

async function failure(response: Response): Promise<ApiError> {
  let message = `The service responded with ${response.status}.`;
  let code: string | undefined;
  let payload: unknown;
  try {
    payload = await response.json();
    const body = payload as { reason?: string; code?: string };
    if (typeof body.reason === "string" && body.reason.length > 0) message = body.reason;
    if (typeof body.code === "string") code = body.code;
  } catch {
    // A body that is not JSON tells us nothing useful; the status already did.
  }
  return new ApiError(response.status, message, code, payload);
}

/** Narrows the 409 the item library answers with. */
export function itemDeleteRefusal(error: unknown): ItemDeleteRefusal | null {
  if (!(error instanceof ApiError) || error.code !== "itemReferenced") return null;
  const body = error.body as ItemDeleteRefusal | undefined;
  return body && Array.isArray(body.references) ? body : null;
}

interface RequestOptions {
  readonly method?: string;
  readonly body?: BodyInit;
  readonly contentType?: string;
  readonly bearer?: string;
  readonly headers?: Record<string, string>;
}

async function request(path: string, options: RequestOptions = {}): Promise<Response> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json", ...options.headers };
  if (options.contentType) headers["Content-Type"] = options.contentType;
  if (options.bearer) headers.Authorization = `Bearer ${options.bearer}`;
  if (method !== "GET" && method !== "HEAD") {
    const token = csrfToken();
    if (token) headers["X-CSRF-Token"] = token;
  }

  const response = await fetch(path, {
    method,
    headers,
    // The session lives in a cookie; nothing here holds a token.
    credentials: "same-origin",
    ...(options.body === undefined ? {} : { body: options.body }),
  });
  if (!response.ok) throw await failure(response);
  return response;
}

async function json<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await request(path, options);
  return (await response.json()) as T;
}

export const api = {
  // MARK: - Sign in

  requestPairingCode: () => json<PairingRequest>("/api/auth/pairing", { method: "POST" }),

  /** `PENDING` while the phone has not confirmed; throws `ApiError(410)` once dead. */
  async pollPairing(code: string): Promise<{ userId: string } | typeof PENDING> {
    const response = await request(`/api/auth/pairing/${encodeURIComponent(code)}`);
    if (response.status === 204) return PENDING;
    return (await response.json()) as { userId: string };
  },

  logout: () => request("/api/sessions/logout", { method: "POST" }),

  // MARK: - Collections

  collections: () => json<readonly CollectionSummary[]>("/api/collections"),

  collection: (id: string) => json<CollectionDetail>(`/api/collections/${id}`),

  createCollection: (name: string) =>
    json<CollectionDetail>("/api/collections", {
      method: "POST",
      contentType: "application/json",
      body: JSON.stringify({ name }),
    }),

  /** Clones the shipped sample into this account, with its own item ids. */
  cloneSample: () => json<ImportResponse>("/api/collections/sample", { method: "POST" }),

  importBundle: (bundle: string, name?: string) =>
    json<ImportResponse>(
      `/api/collections/import${name ? `?name=${encodeURIComponent(name)}` : ""}`,
      { method: "POST", contentType: "application/json", body: bundle },
    ),

  deleteCollection: (id: string) => request(`/api/collections/${id}`, { method: "DELETE" }),

  validate: (id: string) => json<GateReport>(`/api/collections/${id}/validate`, { method: "POST" }),

  /** The draft document plus the version to send back in `If-Match`. */
  async draft(id: string): Promise<{ document: unknown; version: number }> {
    const response = await request(`/api/collections/${id}/draft`);
    const version = Number(response.headers.get("x-draft-version") ?? "0");
    return { document: await response.json(), version };
  },

  /** Returns the new version; `null` when someone else saved first. */
  async saveDraft(id: string, version: number, document: unknown): Promise<number | null> {
    try {
      const response = await request(`/api/collections/${id}/draft`, {
        method: "PATCH",
        contentType: "application/json",
        headers: { "If-Match": String(version) },
        body: JSON.stringify(document),
      });
      return Number(response.headers.get("x-draft-version") ?? String(version + 1));
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) return null;
      throw error;
    }
  },

  checkFormula: (id: string, expression: string, parameters: Record<string, number | boolean | string>) =>
    json<FormulaCheck>(`/api/collections/${id}/formula`, {
      method: "POST",
      contentType: "application/json",
      body: JSON.stringify({ expression, parameters }),
    }),

  /** The engine's own answer for a group rule's tags. */
  groupMatch: (id: string, tags: readonly string[], matchAll: boolean) =>
    json<GroupMatchResponse>(`/api/collections/${id}/group-match`, {
      method: "POST",
      contentType: "application/json",
      body: JSON.stringify({ tags, matchAll }),
    }),

  preview: (id: string, templateId: string, parameters: Record<string, number | boolean | string>) =>
    json<PreviewResult>(`/api/collections/${id}/preview`, {
      method: "POST",
      contentType: "application/json",
      body: JSON.stringify({ templateId, parameters }),
    }),

  publishDiff: (id: string) => json<PublishDiffResponse>(`/api/collections/${id}/publish-diff`),

  publish: (id: string) => json<PublishResponse>(`/api/collections/${id}/publish`, { method: "POST" }),

  unpublish: (id: string) => request(`/api/collections/${id}/unpublish`, { method: "POST" }),

  /** Throws the draft away and starts again from what subscribers are getting. */
  revertDraft: (id: string) => json<CollectionDetail>(`/api/collections/${id}/revert-draft`, { method: "POST" }),

  revisions: (id: string) => json<readonly Revision[]>(`/api/collections/${id}/revisions`),

  rollback: (id: string, revisionNumber: number) =>
    json<PublishResponse>(`/api/collections/${id}/revisions/${revisionNumber}/rollback`, {
      method: "POST",
    }),

  rotateAccessCode: (id: string, clear: boolean) =>
    json<AccessCodeResponse>(`/api/collections/${id}/access-code`, {
      method: "POST",
      contentType: "application/json",
      body: JSON.stringify(clear ? { clear: true } : {}),
    }),

  // MARK: - Item library

  items: (query: string) =>
    json<readonly LibraryItem[]>(`/api/items${query ? `?q=${encodeURIComponent(query)}` : ""}`),

  createItem: (draft: ItemDraft) =>
    json<LibraryItem>("/api/items", {
      method: "POST",
      contentType: "application/json",
      body: JSON.stringify(draft),
    }),

  /** Only the keys given are changed; an explicit null clears one. */
  updateItem: (id: string, patch: Record<string, unknown>) =>
    json<LibraryItem>(`/api/items/${id}`, {
      method: "PATCH",
      contentType: "application/json",
      body: JSON.stringify(patch),
    }),

  deleteItem: (id: string) => request(`/api/items/${id}`, { method: "DELETE" }),

  itemReferences: (id: string) => json<readonly ItemReference[]>(`/api/items/${id}/references`),

  // MARK: - Public

  publicMeta: (publicId: string, accessCode: string | null | undefined) =>
    json<PublicMeta>(
      `/api/public/c/${encodeURIComponent(publicId)}/meta`,
      accessCode ? { bearer: accessCode } : {},
    ),

  // MARK: - Account

  account: () => json<AccountSummary>("/api/account"),

  devices: () => json<readonly Device[]>("/api/devices"),

  revokeDevice: (id: string) => request(`/api/devices/${id}`, { method: "DELETE" }),

  sessions: () => json<readonly BrowserSession[]>("/api/sessions"),

  revokeSession: (id: string) => request(`/api/sessions/${id}`, { method: "DELETE" }),

  /** Returns the new code once; the old one stops working immediately. */
  rotateRecoveryCode: () =>
    json<RecoveryCodeResponse>("/api/account/recovery-code", { method: "POST" }),

  /** `confirm` is the word the author typed; the server checks it too. */
  deleteAccount: (confirm: string) =>
    json<DeletionResponse>("/api/account", {
      method: "DELETE",
      contentType: "application/json",
      body: JSON.stringify({ confirm }),
    }),
};
